import React, { useState, useEffect } from 'react';
/**
 * Decode RFC2047 encoded-word strings like "=?charset?Q?text?=" or "=?charset?B?base64?="
 */
function decodeMimeWords(input: string): string {
  return input.replace(/=\?([^?]+)\?([BQbq])\?([^?]+)\?=/g, (_, _charset, encoding, text) => {
    if (encoding.toUpperCase() === 'B') {
      try {
        return atob(text);
      } catch {
        return text;
      }
    } else {
      return text
        .replace(/_/g, ' ')
        .replace(/=([A-Fa-f0-9]{2})/g, (_: string, hex: string) =>
          String.fromCharCode(parseInt(hex, 16))
        );
    }
  });
}

// Define a type for the thread object
interface Thread {
  id: string;
  subject: string;
  senderString?: string;
  recipientString?: string;
  createdAt: string;
  isRead?: boolean;
}

export const ThreadList: React.FC<any> = ({
  threads,
  onSelect,
  page,
  limit,
  totalCount,
  onPageChange,
  onLimitChange,
}) => {
  // Local state to mark messages as read without calling API
  const [localThreads, setLocalThreads] = useState<Thread[]>(threads);
  useEffect(() => setLocalThreads(threads), [threads]);
  
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  return (
    <div className="w-full h-full flex flex-col bg-white text-sm">
      {/* pagination controls - Mobile friendly */}
      <div className="flex flex-col sm:flex-row items-center justify-between p-3 border-b border-gray-200 flex-shrink-0 gap-2">
        <div className="flex items-center">
          <label className="text-xs">
            Show
            <select
              value={limit}
              onChange={e => { 
                const newLimit = parseInt(e.target.value, 10);
                onLimitChange(newLimit); 
              }}
              className="mx-1 text-xs border rounded px-1 py-0.5"
            >
              {[10, 20, 50].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            per page
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 text-xs bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300"
            onClick={() => {
              if (hasPrevPage) {
                onPageChange(page - 1);
              }
            }}
            disabled={!hasPrevPage}
          >
            ←
          </button>
          <span className="text-xs min-w-16 text-center">
            {totalCount === 0 ? '0 / 0' : (
              <span title={`Showing ${((page - 1) * limit) + 1}-${Math.min(page * limit, totalCount)} of ${totalCount} messages`}>
                {page} / {totalPages}
              </span>
            )}
          </span>
          <button
            className="px-3 py-1 text-xs bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300"
            onClick={() => {
              if (hasNextPage) {
                onPageChange(page + 1);
              }
            }}
            disabled={!hasNextPage}
          >
            →
          </button>
        </div>
      </div>

      {localThreads.length === 0 ? (
        <div className="text-center text-gray-400 pt-10 mb-10">
          {totalCount === 0 ? 'No messages to display' : 'Loading messages...'}
        </div>
      ) : (
        /* Scrolling list fixed to container height */
        <div className="flex-1 overflow-y-auto p-3">
          <div className="space-y-2">
            {localThreads.map((thread: Thread, index: number) => {
              const isSelected = false;
              const isVisited = false;
              const bgClass = isSelected
                ? 'bg-blue-50'
                : isVisited
                  ? 'bg-gray-100'
                  : 'bg-white';
              const textClass = isSelected
                ? 'font-semibold'
                : thread.isRead
                ? ''
                : 'font-bold';

              const fromText = thread.senderString || 'Unknown';
              const toText = thread.recipientString || 'Unknown';

              return (
                <div
                  key={`${thread.id ?? 'thread'}-${index}`}
                  className={
                    `cursor-pointer rounded-md px-4 py-3
                    border border-gray-200 shadow-sm hover:shadow-md
                    transition-all ${bgClass} ${textClass}`
                  }
                  onClick={() => {
                    // Mark as read locally
                    setLocalThreads((prev: Thread[]) => prev.map((t: Thread) =>
                      t.id === thread.id ? { ...t, isRead: true } : t
                    ));
                    onSelect(thread);
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="truncate text-gray-800 font-medium">
                        {decodeMimeWords(thread.subject)}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        From: {decodeMimeWords(fromText)}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 sm:pl-3 whitespace-nowrap self-start sm:self-center">
                      {new Date(thread.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
